import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import shortid from "shortid";

import { delayForIndexing } from "@examspark/common";
import {
  dateRange,
  fromAWSDate,
  toAWSDate,
} from "@examspark/common/dist/utils/coursePlanner";
import {
  useAppContext,
  useMutationDeleteCourseCalendar,
  useMutationUpdateCourseCalendar,
  useQueryGetCourseCalendar,
} from "@examspark/common-app";
import {
  CautionBanner,
  ConfirmModal,
  FormContext,
  toast,
} from "@examspark/common-ui";
import { useArrayReducer } from "@examspark/common-ui/dist/hooks/useArray";
import {
  AcademicCalendar,
  Course,
  CourseCalendar,
  NonInstructionDate,
  ResourceVisibilityType,
  UnitPlan,
} from "@examspark/types";

import { CourseCalendarForm } from "./CourseCalendarForm";

export const EditCourseCalendarRoute = () => {
  const { courseId, courseCalendarId } = useParams<{
    courseId: string;
    courseCalendarId: string;
  }>();

  return (
    <EditCourseCalendar
      courseCalendarId={courseCalendarId}
      courseId={courseId}
    />
  );
};

export const EditCourseCalendar = ({
  courseCalendarId,
  courseId,
}: {
  courseCalendarId: string | undefined;
  courseId: string | undefined;
}) => {
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course>();
  const [courseCalendar, setCourseCalendar] = useState<CourseCalendar>();
  const [academicCalendar, setAcademicCalendar] = useState<AcademicCalendar>();
  const [name, setName] = useState<string>();
  const holidays = useArrayReducer<NonInstructionDate & { id: string }>([]);
  const [plan, setPlan] = useState<UnitPlan[]>();
  const [visibility, setVisibility] = useState<ResourceVisibilityType | null>(
    null,
  );
  const [context] = useAppContext();

  const courseCalendarQuery = useQueryGetCourseCalendar(
    courseCalendarId,
    courseId,
  );

  useEffect(() => {
    if (courseCalendarQuery.data) {
      setCourse(courseCalendarQuery.data.course);
      setCourseCalendar(courseCalendarQuery.data);
      setAcademicCalendar(courseCalendarQuery.data.calendar);
      setName(courseCalendarQuery.data.name ?? undefined);
      holidays.onSet(
        courseCalendarQuery.data.additionalNonInstructionDates.map(
          (holiday) => ({
            ...holiday,
            id: shortid.generate(),
          }),
        ),
      );
      setPlan(courseCalendarQuery.data.units);
      setVisibility(courseCalendarQuery.data.visibility ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseCalendarQuery.data]);

  const updateMutation = useMutationUpdateCourseCalendar();
  const deleteCalendarMutation = useMutationDeleteCourseCalendar();

  const workspaceId = context.workspaceUser?.workspace?.id;
  const isOwnCalendar = courseCalendar?.workspaceId === workspaceId;

  // do some recalculating to account for additional non-instructional days.
  // these will get materialized into the unitPlan so we don't have to worry
  // about them later.
  const fullPlan = plan?.map((unit) => ({
    ...unit,
    dates: unit.dates?.filter((date) => {
      return !holidays.values
        .flatMap((holiday) =>
          holiday.endAt
            ? [
                ...dateRange(
                  fromAWSDate(holiday.startAt),
                  fromAWSDate(holiday.endAt),
                ),
              ].map(toAWSDate)
            : [holiday.startAt],
        )
        .includes(date);
    }),
  }));

  const client = useQueryClient();

  const handleDelete = async () => {
    await ConfirmModal.show({
      onResolving: async (result) => {
        if (!result) return;
        if (!courseCalendarId) return;
        if (!isOwnCalendar) {
          toast.error("You cannot delete a calendar from another workspace");
          return;
        }

        await deleteCalendarMutation.mutateAsync({
          key: {
            id: courseCalendarId,
            sk: `course-calendar#${courseId}#v0`,
          },
        });
        await delayForIndexing();
        toast.success("Calendar Deleted");
        await Promise.all([
          client.invalidateQueries(["academic-calendar"]),
          client.invalidateQueries(["academic-calendars"]),
          client.invalidateQueries(["course-calendar"]),
          client.invalidateQueries(["course-calendars"]),
        ]);
        navigate("/calendar/course");
      },
      children: (
        <div style={{ padding: "1rem" }}>
          <CautionBanner variant="plain">
            Are you sure you want to delete this calendar? This action cannot be
            undone.
          </CautionBanner>
        </div>
      ),
    });
  };

  const handleArchive = async () => {
    await ConfirmModal.show({
      onResolving: async (result) => {
        if (!result) return;

        if (!isOwnCalendar) {
          toast.error("You cannot archive a calendar from another workspace");
          return;
        }

        const { id, sk, workspaceId } = courseCalendarQuery.data ?? {};

        if (!id || !sk || !workspaceId) {
          alert("missing required fields");
          return;
        }

        await updateMutation.mutateAsync({
          id,
          sk,
          workspaceId,
          isArchived: true,
        });

        await delayForIndexing();
        toast.success("Calendar Archived");
        await Promise.all([
          client.invalidateQueries(["academic-calendar"]),
          client.invalidateQueries(["academic-calendars"]),
          client.invalidateQueries(["course-calendar"]),
        ]);
        navigate("/calendar/course");
      },
      children: (
        <div style={{ padding: "1rem" }}>
          <CautionBanner variant="plain">
            You are about to archive the calendar {name} in your workspace. It
            will still be visible but can no longer be used.
            <br />
            <br />
            Are you sure you want to archive this calendar?
          </CautionBanner>
        </div>
      ),
    });
  };

  const handleSave = async () => {
    const { id, sk, workspaceId } = courseCalendarQuery.data ?? {};

    if (!isOwnCalendar) {
      toast.error("You cannot edit a calendar from another workspace");
      return;
    }

    if (!fullPlan || !workspaceId || !id || !sk) {
      alert("missing required fields");
      return;
    }

    try {
      const sortedHolidays = holidays.values
        .map((holiday) => ({
          startAt: holiday.startAt,
          endAt: holiday.endAt,
          label: holiday.label,
          type: holiday.type,
        }))
        .sort(
          (a, b) =>
            a.startAt.localeCompare(b.startAt) ||
            (a.label ?? "").localeCompare(b.label ?? ""),
        );

      await updateMutation.mutateAsync({
        id,
        sk,
        workspaceId,

        visibility: visibility,

        name,
        units: fullPlan,
        additionalNonInstructionDates: sortedHolidays,
      });

      toast.success("Calendar Updated");
    } catch (err) {
      toast.error("Error updating calendar");
      console.error(err);
      return;
    }
  };

  return (
    <FormContext.Provider value={{ disabled: !isOwnCalendar }}>
      <CourseCalendarForm
        action="Edit"
        course={[course, setCourse]}
        calendar={[academicCalendar, setAcademicCalendar]}
        name={[name, setName]}
        holidays={holidays}
        plan={[fullPlan, setPlan]}
        visibility={[visibility, setVisibility]}
        onDelete={isOwnCalendar ? handleDelete : undefined}
        onArchive={isOwnCalendar ? handleArchive : undefined}
        onSave={handleSave}
      />
    </FormContext.Provider>
  );
};
